import * as React from 'react';

import { DropdownButton } from 'src/components/DropdownButton';
import { IconButton, type IconButtonProps } from 'src/components/IconButton';
import { MenuItem } from 'src/components/MenuItem';
import { UnifyIcons } from 'src/components/UnifyIcons';
import type { FileModel } from 'src/components/FileUploader/fileModel';
import type { FileUploaderProps } from 'src/components/FileUploader';
import type { FileListItemProps } from 'src/components/FileListItem/FileListItem';

type FileListItemActionMenuOwnProps = {
  fileIsUploading: boolean;
  rootId?: string;
};

type FileListItemActionMenuProps = FileListItemActionMenuOwnProps &
  Pick<FileModel, 'file'> &
  Pick<
    FileUploaderProps,
    'showFileSize' | 'renderFileListItemActionMenuItems'
  > &
  Pick<FileListItemProps, 'onRemove'>;

export const FileListItemActionMenu = React.memo(
  function FileListItemActionMenu(
    props: FileListItemActionMenuProps
  ): React.ReactElement | null {
    const {
      file,
      fileIsUploading,
      renderFileListItemActionMenuItems,
      onRemove,
      rootId,
    } = props;

    const iconButtonRemovesFileWhenClicked = !renderFileListItemActionMenuItems;

    // TODO: intl these strings
    const removeFileLabel = 'Remove File';
    const actionMenuButtonLabel = fileIsUploading
      ? 'Cancel Upload'
      : iconButtonRemovesFileWhenClicked
      ? removeFileLabel
      : 'Options';
    const actionButtonSize = 'xsmall' satisfies IconButtonProps['size'];

    const iconButtonProps: Partial<IconButtonProps> = {
      size: actionButtonSize,
      'aria-controls': iconButtonRemovesFileWhenClicked ? rootId : undefined,
      'aria-label': actionMenuButtonLabel,
      onClick: iconButtonRemovesFileWhenClicked
        ? () => onRemove?.(file)
        : undefined,
    };

    if (fileIsUploading || renderFileListItemActionMenuItems === undefined) {
      return (
        <IconButton {...iconButtonProps}>
          <UnifyIcons.Close />
        </IconButton>
      );
    }

    const removeFileMenuItemElement = (
      <MenuItem
        key="removeFileMenuItemElement"
        aria-controls={rootId}
        onClick={() => onRemove?.(file)}
      >
        {removeFileLabel}
      </MenuItem>
    );

    return (
      <DropdownButton
        component={IconButton}
        additionalButtonProps={iconButtonProps}
        displayText={actionMenuButtonLabel}
        size={actionButtonSize}
        placement="bottom-end"
        endIcon={<UnifyIcons.MoreVert />}
        endIconWhenOpen={<UnifyIcons.MoreVert />}
      >
        {renderFileListItemActionMenuItems(removeFileMenuItemElement, file)}
      </DropdownButton>
    );
  }
);

export default FileListItemActionMenu;
